/**
 * ConvertInteractionToCaseModal
 * Modal for converting an interaction to a case
 */

import { useState, useEffect } from 'react';
import { FileText, Briefcase, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { interactionService, type ConvertToCaseData } from '@/services/interaction-service';
import toast from '@/lib/toast';

import { getErrorMessage } from '../../utils/error.utils'
export interface ConvertInteractionToCaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (newCase: { id: string }) => void;
  interaction: {
    id: string;
    subject: string;
    description?: string;
  } | null;
}

export function ConvertInteractionToCaseModal({
  isOpen,
  onClose,
  onSuccess,
  interaction
}: ConvertInteractionToCaseModalProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<ConvertToCaseData>({
    title: '',
    description: '',
    practiceArea: '',
    caseType: ''
  });

  // Reset form when modal opens/interaction changes
  useEffect(() => {
    if (isOpen && interaction) {
      setFormData({
        title: interaction.subject || '',
        description: interaction.description || '',
        practiceArea: '',
        caseType: ''
      });
    }
  }, [isOpen, interaction]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!interaction) return;

    setLoading(true);
    try {
      const response = await interactionService.convertToCase(
        interaction.id,
        formData
      );

      toast.success('Atendimento convertido em Caso com sucesso!');

      if (onSuccess && typeof response.data?.id === 'string') {
        onSuccess({ id: response.data.id });
      }

      handleClose();
    } catch (error: unknown) {
      toast.error(
        getErrorMessage(error, 'Erro ao converter atendimento em caso')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      practiceArea: '',
      caseType: ''
    });
    onClose();
  };

  const handleChange = (field: keyof ConvertToCaseData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent size="lg" fullScreenOnMobile className="max-sm:overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Converter Atendimento em Caso</DialogTitle>
          <DialogDescription>
            O atendimento será marcado como convertido e um novo caso será criado
          </DialogDescription>
        </DialogHeader>
        {interaction && (
          <div className="rounded-lg border bg-muted/40 p-3">
            <div className="flex items-start gap-3">
              <FileText className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <h4 className="mb-1 font-medium">Atendimento de Origem</h4>
                <p className="break-words text-sm text-muted-foreground">
                  <strong>Assunto:</strong> {interaction.subject}
                </p>
                {interaction.description && (
                  <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                    <strong>Descrição:</strong> {interaction.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <div role="note" className="rounded-lg border bg-muted/40 p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">
              Após a conversão, o atendimento não poderá mais ser editado e aparecerá como &quot;Convertido em Caso&quot; na lista de atendimentos.
            </p>
          </div>
        </div>

        <form id="convert-to-case-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="convert-case-title">
              Título do Caso <span className="text-destructive">*</span>
            </Label>
            <Input
              id="convert-case-title"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Digite o título do caso..."
              required
            />
            <p className="text-xs text-muted-foreground">
              Por padrão, usa o assunto do atendimento. Você pode modificar se necessário.
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="convert-case-description">Descrição</Label>
            <Textarea
              id="convert-case-description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="resize-none"
              rows={4}
              placeholder="Descreva os detalhes do caso..."
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="convert-case-area">Área de Atuação</Label>
              <Input
                id="convert-case-area"
                value={formData.practiceArea}
                onChange={(e) => handleChange('practiceArea', e.target.value)}
                placeholder="Ex: Trabalhista, Cível, etc."
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="convert-case-type">Tipo de Caso</Label>
              <Input
                id="convert-case-type"
                value={formData.caseType}
                onChange={(e) => handleChange('caseType', e.target.value)}
                placeholder="Ex: Consultoria, Contencioso, etc."
              />
            </div>
          </div>

          <div className="rounded-lg border p-3">
            <div className="flex items-start gap-3">
              <Briefcase className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <div>
                <h4 className="mb-1 font-medium">O que será criado:</h4>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>• Um novo caso com as informações fornecidas</li>
                  <li>• Vínculo automático com o atendimento original</li>
                  <li>• Evento na timeline do cliente</li>
                  <li>• Badge de origem &quot;De Atendimento&quot; no caso</li>
                </ul>
              </div>
            </div>
          </div>
        </form>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            form="convert-to-case-form"
            disabled={loading || !formData.title?.trim()}
          >
            {loading ? 'Convertendo...' : 'Converter em Caso'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
